import {
  solverRelayGetStatus,
  solverRelayWaitForSettlement,
} from "@src/actions/solverRelayProxy"
import { logger } from "@src/utils/logger"
import { type ActorRef, type Snapshot, log } from "xstate"
import { assign, fromCallback, fromPromise, not, setup } from "xstate"
import type { TokenInfo } from "../../types/base"
import { getTxStatus, submitTxHash } from "./1cs"

/** Max auto-retries for transient checkTxStatus errors before surfacing failure */
const MAX_STATUS_RETRIES = 10
/** Delay (ms) between retries */
const RETRY_DELAY_MS = 3000

export type OneClickExpiredEvent = {
  type: "ONE_CLICK_EXPIRED"
  data: {
    depositAddress: string
  }
}
type ChildEvent =
  | {
      type: "ONE_CLICK_SETTLED"
      data: {
        depositAddress: string
        status: string
        tokenIn: TokenInfo
        tokenOut: TokenInfo
      }
    }
  | OneClickExpiredEvent
type ParentActor = ActorRef<Snapshot<unknown>, ChildEvent>

export const oneClickStatusMachine = setup({
  types: {
    input: {} as {
      parentRef: ParentActor
      // null for confidential deposit (no intent involved — only an origin-chain tx).
      intentHash: string | null
      depositAddress: string
      /** Required for memo-based origin deposits (e.g. Stellar) when polling /v0/status. */
      depositMemo?: string | null
      tokenIn: TokenInfo
      tokenOut: TokenInfo
      totalAmountIn: { amount: bigint; decimals: number }
      totalAmountOut: { amount: bigint; decimals: number }
      isConfidential?: boolean
      // ISO deadline after which a still-pending deposit address expires and
      // polling should stop. null = never expires (withdraw/swap/active deposit).
      deadline?: string | null
    },
    context: {} as {
      parentRef: ParentActor
      intentHash: string | null
      depositAddress: string
      depositMemo: string | null
      txHash: string | null
      tokenIn: TokenInfo
      tokenOut: TokenInfo
      totalAmountIn: { amount: bigint; decimals: number }
      totalAmountOut: { amount: bigint; decimals: number }
      isConfidential: boolean
      deadline: string | null
      status: string | null
      error: Error | null
      retryCount: number
    },
  },
  actions: {
    logError: (_, params: { error: unknown }) => {
      logger.error(params.error)
    },
    setTxHash: assign({
      txHash: (_, txHash: string) => txHash,
    }),
    setStatus: assign({
      status: (_, status: string) => status,
    }),
    setError: assign({
      error: (_, error: Error) => error,
    }),
    clearError: assign({
      error: null,
    }),
    incrementRetryCount: assign({
      retryCount: ({ context }) => context.retryCount + 1,
    }),
    resetRetryCount: assign({
      retryCount: 0,
    }),
    notifyParent: ({ context }) => {
      if (context.status && !statusesToTrack.has(context.status)) {
        context.parentRef.send({
          type: "ONE_CLICK_SETTLED",
          data: {
            depositAddress: context.depositAddress,
            status: context.status,
            tokenIn: context.tokenIn,
            tokenOut: context.tokenOut,
          },
        })
      }
    },
    notifyParentExpired: ({ context }) => {
      context.parentRef.send({
        type: "ONE_CLICK_EXPIRED",
        data: { depositAddress: context.depositAddress },
      })
    },
  },
  actors: {
    waitIntentTxHash: fromPromise(
      async ({
        input,
      }: {
        input: { intentHash: string }
      }): Promise<string> => {
        // Poll getStatus for early TX_BROADCASTED (shows explorer link sooner),
        // with waitForSettlement as a fallback in case polling misses it.
        const settlementPromise = solverRelayWaitForSettlement({
          intentHash: input.intentHash,
        }).then((r) => r.txHash)

        const pollForTxHash = async (): Promise<string> => {
          for (let i = 0; i < 60; i++) {
            try {
              const status = await solverRelayGetStatus({
                intentHash: input.intentHash,
              })
              if (
                (status.status === "TX_BROADCASTED" ||
                  status.status === "SETTLED") &&
                "txHash" in status
              ) {
                return status.txHash
              }
              if (status.status === "NOT_FOUND_OR_NOT_VALID") {
                // Intent failed permanently — stop polling, let settlementPromise handle it
                return settlementPromise
              }
            } catch {
              // Transient error (network, timeout) — continue polling
            }
            await new Promise((r) => setTimeout(r, 2000))
          }
          // Polling exhausted, fall through to settlement promise
          return settlementPromise
        }

        return Promise.race([pollForTxHash(), settlementPromise])
      }
    ),
    submitTxHashActor: fromPromise(
      async ({
        input,
      }: {
        input: { depositAddress: string; txHash: string }
      }) => {
        return submitTxHash({
          depositAddress: input.depositAddress,
          txHash: input.txHash,
        })
      }
    ),
    checkTxStatus: fromPromise(
      async ({
        input,
      }: {
        input: {
          depositAddress: string
          depositMemo?: string | null
          isConfidential: boolean
        }
      }) => {
        if (input.isConfidential) {
          const { fetchConfidentialExecutionStatus } = await import(
            "./privateIntents"
          )
          const result = await fetchConfidentialExecutionStatus(
            input.depositAddress,
            input.depositMemo
          )
          if ("err" in result) {
            throw new Error(result.err)
          }
          return result.ok.status
        }
        const result = await getTxStatus(input.depositAddress)
        if ("err" in result) {
          throw new Error(result.err)
        }
        return result.ok.status
      }
    ),
    // Stops a still-pending address (valid ~30 days) from polling forever on a
    // backgrounded tab. sync() on mount covers spawn-while-hidden.
    visibilityListener: fromCallback(({ sendBack }) => {
      if (typeof document === "undefined") return
      const sync = () => {
        sendBack({ type: document.hidden ? "PAUSE" : "RESUME" })
      }
      document.addEventListener("visibilitychange", sync)
      sync()
      return () => document.removeEventListener("visibilitychange", sync)
    }),
  },
  guards: {
    isConfidential: ({ context }) => context.isConfidential,
    shouldContinueTracking: ({ context }) => {
      return context.status != null && statusesToTrack.has(context.status)
    },
    isTerminalFailureStatus: ({ context }) => {
      return (
        context.status != null && terminalFailureStatuses.has(context.status)
      )
    },
    // Expire only while still waiting for a deposit. If one is already detected
    // (KNOWN_DEPOSIT_TX / PROCESSING) keep tracking to its terminal status,
    // regardless of the address deadline.
    isExpiredWithoutDeposit: ({ context }) => {
      if (context.deadline == null) return false
      if (context.status != null && depositDetectedStatuses.has(context.status))
        return false
      return Date.now() >= new Date(context.deadline).getTime()
    },
    hasRetriesLeft: ({ context }) => {
      return context.retryCount < MAX_STATUS_RETRIES
    },
  },
  delays: {
    pollInterval: 500,
    retryDelay: RETRY_DELAY_MS,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QHsB2YDCAbAlgYwGsBlAFwEMSBXWAOgAcxUIdUoBiAbQAYBdRUOslg4SONPxAAPRABYAnHJoAOAMwBWOQEYVKgEwB2fbq4yANCACeiY7pq61XR100A2OVw2alAX2-m0mLiEpBTUNHgAFmCELOwQATQsAG7IBGA0Adj4xORUtJHRBLEIych4FGKo3DzVEoLCouJIUrIKyupaOgZGJuZWCLryNPryCvqackq6E4a+-uhZwblhBTGsbGAATpvIm-RYFABmuwC2GQtBOaH5UWtQJagp5Y1VvLXN9SKVEtIIctN2UZuGQyNT6FSqPrWFy2OT6FxqNRTTTyGQwmRzECZS4hPI0ADuZC+63eAiEXyaoF+mn0ShoCjGuncLk03ShCBk+kULi5bmcmhRKk0YMx2OyuLChOJ7A4mj4H3JLx+iBpdIZcKZXBZbMssgm9KZbmMChhE1FF3Fy1ogiwuHWklguXSZEOJC2AAobVgAJKoN2bJJkLAASjYYqW13oyFtsVJIE+Suav1G7U8XUMxjMuo5+ppcmBKhkUw1KnNgUtka2O02bAASgBRAAqtYAmnGE98kyrHPoDfmeaoQeylJoaKMGSPxkW9L4-CBUMgIHAJOGrnk6orO1TEABaFzsvdlxZrsIMJixDcNLctBAqIypuQyEzTDQGYe9pE6O-yLjM39HnErXCW4ilYS8KVQZUEDcRQ1CFaZVA0JRQV0dkDBkMdDWQotJhRfQAIrPEpVEMCFSvSkbxkVDsxULUaFotEplUJklEfAiIzxL0LzIiCoPUXsdE8VRwRhFl2RkLwaDzFwlBZeFdCUNQZFLOdVwlWhYEoPA8DgeAeMTbdbwk1NWJcPQ1AROQ1HE+EaB5fMFCUx83BcdiT1oKtdnAgybwFfUXF-QsuCUZCdB5dl3BoYKdFBOQ7zw2ZZyAA */
  id: "oneClickStatus",
  initial: "pending",
  invoke: { src: "visibilityListener" },
  on: {
    CANCEL: ".cancelled",
  },
  context: ({ input }) => ({
    parentRef: input.parentRef,
    intentHash: input.intentHash,
    depositAddress: input.depositAddress,
    depositMemo: input.depositMemo ?? null,
    txHash: null,
    tokenIn: input.tokenIn,
    tokenOut: input.tokenOut,
    totalAmountIn: input.totalAmountIn,
    totalAmountOut: input.totalAmountOut,
    isConfidential: input.isConfidential === true,
    deadline: input.deadline ?? null,
    status: null,
    error: null,
    retryCount: 0,
  }),
  states: {
    pending: {
      always: [
        { target: "checking", guard: "isConfidential" },
        { target: "intent_settling" },
      ],
    },
    intent_settling: {
      invoke: {
        src: "waitIntentTxHash",
        input: ({ context }) => {
          // intent_settling is only reachable when isConfidential === false
          // (see `pending.always` guard above); withdraw always supplies an intentHash.
          if (context.intentHash == null) {
            throw new Error(
              "intentHash is required for non-confidential status tracking"
            )
          }
          return { intentHash: context.intentHash }
        },
        onDone: {
          target: "SubmittingTxHash",
          actions: [
            log("Intent tx known"),
            { type: "setTxHash", params: ({ event }) => event.output },
          ],
        },
        onError: {
          target: "error",
          actions: [
            {
              type: "setError",
              params: ({ event }) => new Error(String(event.error)),
            },
            {
              type: "logError",
              params: ({ event }) => event,
            },
          ],
        },
      },
    },
    SubmittingTxHash: {
      invoke: {
        src: "submitTxHashActor",
        input: ({ context }) => {
          if (context.txHash == null) {
            logger.warn(
              "Submitting with null txHash — polling will track by depositAddress",
              {
                depositAddress: context.depositAddress,
                intentHash: context.intentHash,
              }
            )
          }
          return {
            depositAddress: context.depositAddress,
            txHash: context.txHash ?? "",
          }
        },
        onDone: {
          target: "checking",
          actions: log("Intent tx submitted"),
        },
        // Proceed to checking even on submit failure: the 1-Click API tracks
        // transactions by depositAddress independently, so status polling works
        // regardless of whether the txHash submission succeeded.
        onError: {
          target: "checking",
          actions: [
            {
              type: "logError",
              params: ({ event }) => ({
                error: event.error,
                context:
                  "submitTxHash failed — proceeding to poll by depositAddress",
              }),
            },
          ],
        },
      },
    },
    checking: {
      on: { PAUSE: "paused" },
      invoke: {
        src: "checkTxStatus",
        input: ({ context }) => ({
          depositAddress: context.depositAddress,
          depositMemo: context.depositMemo,
          isConfidential: context.isConfidential,
        }),
        onDone: {
          target: "waiting",
          actions: [
            {
              type: "setStatus",
              params: ({ event }) => event.output,
            },
            "clearError",
            "resetRetryCount",
            "notifyParent",
          ],
        },
        onError: [
          {
            target: "retryDelay",
            guard: "hasRetriesLeft",
            actions: [
              {
                type: "logError",
                params: ({ event }) => event,
              },
              "incrementRetryCount",
            ],
          },
          {
            target: "error",
            actions: [
              {
                type: "setError",
                params: ({ event }) => new Error(String(event.error)),
              },
              {
                type: "logError",
                params: ({ event }) => event,
              },
            ],
          },
        ],
      },
    },
    waiting: {
      always: [
        {
          target: "error",
          guard: "isTerminalFailureStatus",
        },
        {
          target: "success",
          guard: not("shouldContinueTracking"),
          actions: log("Swap completed"),
        },
        {
          target: "expired",
          guard: "isExpiredWithoutDeposit",
          actions: log("Deposit address expired — stopping status polling"),
        },
        {
          target: "polling",
        },
      ],
    },
    polling: {
      on: { PAUSE: "paused" },
      after: {
        pollInterval: "checking",
      },
    },
    paused: {
      on: { RESUME: "checking" },
    },
    retryDelay: {
      after: {
        retryDelay: "checking",
      },
    },
    success: {
      type: "final",
    },
    expired: {
      type: "final",
      entry: "notifyParentExpired",
    },
    // Silent by design: the only CANCEL sender pairs it with `clearStatusRefs`,
    // so the parent prunes this ref without a notification from here.
    cancelled: {
      type: "final",
    },
    error: {
      on: {
        RETRY: {
          target: "pending",
          actions: "resetRetryCount",
        },
      },
    },
  },
})

export const oneClickStatuses = {
  KNOWN_DEPOSIT_TX: "Known Deposit Tx",
  PROCESSING: "Processing",
  SUCCESS: "Success",
  REFUNDED: "Refunded",
  FAILED: "Failed",
  PENDING_DEPOSIT: "Pending...",
  INCOMPLETE_DEPOSIT: "Incomplete Deposit",
}

export const statusesToTrack = new Set([
  "KNOWN_DEPOSIT_TX",
  "PENDING_DEPOSIT",
  "INCOMPLETE_DEPOSIT",
  "PROCESSING",
])

const terminalFailureStatuses = new Set([
  "FAILED",
  "REFUNDED",
  "FAILED_EXECUTION",
])

// A deposit is already in flight — keep tracking past the address deadline.
// The remaining tracked statuses (PENDING_DEPOSIT / INCOMPLETE_DEPOSIT) mean
// nothing has arrived yet, so they are eligible for expiry.
const depositDetectedStatuses = new Set(["KNOWN_DEPOSIT_TX", "PROCESSING"])

export type ConfidentialDepositOutcome =
  | "pending"
  | "success"
  | "refunded"
  | "failed"

/**
 * Classify a 1CS execution status. `refunded` is distinct from `failed`: funds
 * are credited back to the confidential balance, so it's not an error state.
 */
export function classifyConfidentialStatus(
  rawStatus: string
): ConfidentialDepositOutcome {
  const status = rawStatus.toUpperCase()
  if (statusesToTrack.has(status)) return "pending"
  if (status === "SUCCESS") return "success"
  if (status === "REFUNDED") return "refunded"
  return "failed"
}
